import { Plus, Star, TrendingUp } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import SortableCategory from "./SortableCategory";
import { t } from "@/lib/i18n";

export default function CategoryNav({
  categories,
  activeId,
  onSelect,
  showFavorites,
  favoritesCount,
  mostUsedCount = 0,
  editing,
  onAdd,
  onEditCategory,
  onDeleteCategory,
  onReorder,
  lang = "en",
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = categories.map((c) => c.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(categories, oldIdx, newIdx);
    onReorder(next);
  };

  return (
    <nav
      className="flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-visible tile-scroll pb-2 md:pb-0"
      data-testid="category-nav"
    >
      <button
        type="button"
        onClick={() => onSelect("__favorites__")}
        className={`cat-pill ${activeId === "__favorites__" ? "active" : ""} shrink-0`}
        data-testid="category-favorites"
      >
        <span className="swatch" style={{ background: "#FF6B47" }} />
        <Star size={18} className="shrink-0" fill="currentColor" />
        <span className="flex-1 truncate">{t(lang, "cat.favorites")}</span>
        <span className="text-xs opacity-70">{favoritesCount}</span>
      </button>

      {mostUsedCount > 0 && (
        <button
          type="button"
          onClick={() => onSelect("__most_used__")}
          className={`cat-pill ${activeId === "__most_used__" ? "active" : ""} shrink-0`}
          data-testid="category-most-used"
        >
          <span className="swatch" style={{ background: "#FFB627" }} />
          <TrendingUp size={18} className="shrink-0" />
          <span className="flex-1 truncate">{t(lang, "cat.most_used")}</span>
          <span className="text-xs opacity-70">{mostUsedCount}</span>
        </button>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={ids}
          strategy={typeof window !== "undefined" && window.matchMedia?.("(min-width: 768px)").matches ? verticalListSortingStrategy : horizontalListSortingStrategy}
        >
          {categories.map((c) => (
            <SortableCategory
              key={c.id}
              cat={c}
              active={activeId === c.id}
              editing={editing}
              onSelect={onSelect}
              onEdit={onEditCategory}
              onDelete={onDeleteCategory}
            />
          ))}
        </SortableContext>
      </DndContext>

      {editing && (
        <button
          type="button"
          onClick={onAdd}
          className="cat-pill shrink-0"
          style={{ borderStyle: "dashed", background: "transparent" }}
          data-testid="category-add"
        >
          <Plus size={18} />
          <span>{t(lang, "cat.add_category")}</span>
        </button>
      )}
    </nav>
  );
}
